/**
 * What one piece of the infrastructure map is (2026-09-03; owner: "nothing
 * is clickable here unlike the product map"). The sibling of PartPanel for
 * a node the server drew from data rather than one the guide named: the
 * facts a reader asks first (what it is, how many, on which build, fed by
 * and worked by what), what the code says it carries (2026-09-06; owner:
 * "product view tells exact schema and gives examples. The infra view needs
 * to do something similar": the routes, the tasks, the models, one of them
 * spelled out), the findings that name it, where it is written, and one
 * way on: put it in the question by name (the words are the reader's; the
 * "Ask about this" button went on 2026-09-06, owner: "User will ask if they
 * need/want to"). Docked along the bottom like PartPanel, and for the same
 * reasons.
 */


import type { RuntimeFinding, RuntimePart } from '../../api/types';
import { formatRelativeTime } from '../../lib/format';
import { useChatStore } from '../../stores/chatStore';
import { AddToQuestionButton } from './AddToQuestionButton';
import { DockedPanel, SectionLabel } from './DockedPanel';
import { FindingButton } from './FindingButton';

const KIND_WORD: Record<string, string> = {
  service: 'service',
  queue: 'queue',
  schedule: 'schedule',
  datastore: 'data store',
  external: 'outside system',
  edge: 'connection',
};

/** A fold ("11 workers") stands for many entities and cannot be named with @. */
export function isFold(part: RuntimePart): boolean {
  return part.id.startsWith('svc:_') || part.id.startsWith('queue:_');
}

/** "and 12 more" after a sample of a longer list. */
function more(total: number, shown: number): string | null {
  return total > shown ? `and ${total - shown} more` : null;
}

export function RuntimePartPanel({
  part,
  env,
  findings,
  sampledAt,
  boardParts = [],
  quotes = [],
  onAsk,
  onClose,
}: {
  part: RuntimePart;
  env: string;
  /** the board's parts whose "runs on" phrase named this part: what of the answer lives here */
  boardParts?: ReadonlyArray<{ label: string; what: string }>;
  /** what the answer itself said about this part, by name */
  quotes?: readonly string[];
  /** every finding for the environment; the ones naming this part are shown */
  findings: readonly RuntimeFinding[];
  sampledAt: string | null;
  /** stage a question into the composer */
  onAsk: (question: string) => void;
  onClose: () => void;
}) {
  const mine = findings.filter((f) => part.findings.includes(f.id));
  const requestComposer = useChatStore((s) => s.requestComposer);
  const code = part.code ?? null;
  // A cut narrowed the code to the answer's files (2026-09-06; owner: "it
  // should be related to the chat"): the headings say so.
  const inQuestion = code?.scope === 'question' ? ', in this question' : '';

  return (
    <DockedPanel
      testId="runtime-part-panel"
      attrs={{ 'data-part-id': part.id }}
      ariaLabel={`About ${part.label}`}
      focusKey={part.id}
      kindWord={KIND_WORD[part.kind] ?? part.kind}
      title={part.label}
      titleTestId="runtime-part-title"
      chips={
        <>
          <span className="shrink-0 rounded-chip border border-default px-1.5 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
            {env}
          </span>
          {sampledAt !== null ? (
            <span className="shrink-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
              live numbers as of {formatRelativeTime(sampledAt)}
            </span>
          ) : null}
        </>
      }
      what={part.what}
      onClose={onClose}
      closeTestId="runtime-part-close"
      action={
        !isFold(part) ? (
          <AddToQuestionButton
            testId="runtime-part-add"
            onClick={() =>
              requestComposer({
                kind: 'insert',
                candidates: [
                  {
                    key: `entity:${part.id}`,
                    kind: 'entity',
                    id: part.id,
                    label: part.label,
                    hint: part.kind,
                  },
                ],
              })
            }
          />
        ) : null
      }
    >
        {boardParts.length > 0 ? (
          <div data-testid="runtime-part-in-answer" className="mt-3">
            <SectionLabel>In this answer, it runs</SectionLabel>
            <ul className="mt-1 mb-0 list-none space-y-1 pl-0">
              {boardParts.map((b) => (
                <li key={b.label} className="text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary">
                  <span className="font-medium text-primary">{b.label}</span>: {b.what}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {quotes.length > 0 ? (
          <div data-testid="runtime-part-quotes" className="mt-3">
            <SectionLabel>From the answer</SectionLabel>
            <ul className="mt-1 mb-0 list-none space-y-1 pl-0">
              {quotes.map((q) => (
                <li key={q} className="text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary">
                  &ldquo;{q}&rdquo;
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {part.facts.length > 0 ? (
          <dl data-testid="runtime-part-facts" className="mt-3 mb-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {part.facts.map(([name, value]) => (
              <div key={name} className="contents">
                <dt className="text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-tertiary">{name}</dt>
                <dd className="mt-0 mb-0 ml-0 min-w-0 break-words text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-primary">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {code !== null && code.routes.length > 0 ? (
          <div data-testid="runtime-part-routes" className="mt-3">
            <SectionLabel>{`What it serves${inQuestion}`}</SectionLabel>
            <ul className="mt-1 mb-0 list-none pl-0">
              {code.routes.map((r) => (
                <li key={`${r.method} ${r.path}`} className="mono mt-0.5 break-all text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-secondary">
                  <span className="text-primary">{r.method}</span> {r.path}
                  {r.handler.length > 0 ? <span className="text-tertiary"> · {r.handler}</span> : null}
                </li>
              ))}
              {more(code.routes_total, code.routes.length) !== null ? (
                <li className="mt-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
                  {more(code.routes_total, code.routes.length)}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {code !== null && code.tasks.length > 0 ? (
          <div data-testid="runtime-part-tasks" className="mt-3">
            <SectionLabel>{`${part.kind === 'queue' || part.kind === 'edge' ? 'What travels on it' : 'What it runs'}${inQuestion}`}</SectionLabel>
            <ul className="mt-1 mb-0 list-none pl-0">
              {code.tasks.map((t) => (
                <li key={t.name} className="mono mt-0.5 break-all text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-secondary">
                  {t.signature}
                  {t.queue !== null && part.kind !== 'queue' ? <span className="text-tertiary"> · queue {t.queue}</span> : null}
                </li>
              ))}
              {more(code.tasks_total, code.tasks.length) !== null ? (
                <li className="mt-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
                  {more(code.tasks_total, code.tasks.length)}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {code !== null && code.models.length > 0 ? (
          <div data-testid="runtime-part-models" className="mt-3">
            <SectionLabel>{`What it stores${inQuestion}`}</SectionLabel>
            <ul className="mt-1 mb-0 list-none pl-0">
              {code.models.map((m) => (
                <li key={m.name} className="mono mt-0.5 break-words text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-secondary">
                  <span className="text-primary">{m.name}</span>
                  {m.fields.length > 0 ? ` (${m.fields.join(', ')})` : ''}
                </li>
              ))}
              {more(code.models_total, code.models.length) !== null ? (
                <li className="mt-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
                  {more(code.models_total, code.models.length)}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {code !== null &&
        code.scope === 'question' &&
        code.routes.length === 0 &&
        code.tasks.length === 0 &&
        code.models.length === 0 ? (
          <p
            data-testid="runtime-part-elsewhere"
            className="mt-3 mb-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
          >
            None of its code is in this question: {code.elsewhere_total} route
            {code.elsewhere_total === 1 ? '' : 's'}, task{code.elsewhere_total === 1 ? '' : 's'} or model
            {code.elsewhere_total === 1 ? '' : 's'} elsewhere.
          </p>
        ) : code !== null && code.scope === 'question' && code.elsewhere_total > 0 ? (
          <p className="mt-1 mb-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
            {code.elsewhere_total} more outside this question.
          </p>
        ) : null}

        {code !== null && code.example !== null ? (
          <div className="mt-3">
            <SectionLabel>One of them</SectionLabel>
            <pre
              data-testid="runtime-part-example"
              className="lm-code-wrap mono mt-1 mb-0 overflow-x-auto rounded-card border border-default bg-inset p-2"
            >
              {code.example}
            </pre>
          </div>
        ) : null}

        {mine.length > 0 ? (
          <div className="mt-3">
            <SectionLabel>What the analysis found here</SectionLabel>
            <ul data-testid="runtime-part-findings" className="mt-1 mb-0 list-none space-y-1 pl-0">
              {mine.map((f) => (
                <li key={f.id}>
                  <FindingButton finding={f} onAsk={onAsk} testId="runtime-part-finding" />
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {part.evidence.length > 0 ? (
          <div className="mt-3">
            <SectionLabel>Where this is written</SectionLabel>
            <ul data-testid="runtime-part-evidence" className="mt-1 mb-0 list-none pl-0">
              {part.evidence.map((receipt) => (
                <li key={receipt} className="mono mt-0.5 break-all text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-secondary">
                  {receipt}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
    </DockedPanel>
  );
}
